// src/hooks/useConnectedStores.js
import { useCallback, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Multi-account plumbing shared by the Seller Central and Vendor Central cards.
 *
 * Both connections store a list of accounts with one active "main" at a time and
 * expose the same three endpoints, differing only in the base path and the id
 * field ("seller_id" vs "vendor_id"). Keeping the list, the switch and the
 * scoped disconnect here means the two cards cannot drift apart.
 *
 * @param {object}   config
 * @param {string}   config.basePath   e.g. '/auth/amazon' or '/auth/vendor-central'
 * @param {string}   config.idField    'seller_id' | 'vendor_id'
 * @param {Function} config.getToken   returns the current auth token
 * @param {Function} [config.onChange] called after any change that alters the
 *                                     connection, with the new active id (or null)
 */
export const useConnectedStores = ({ basePath, idField, getToken, onChange }) => {
    const [stores, setStores] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [isSwitching, setIsSwitching] = useState(false);
    const [error, setError] = useState('');

    const activeKey = `active_${idField}`;

    const call = useCallback(async (path, { method = 'GET', body } = {}) => {
        const token = getToken();
        if (!token) throw new Error('Missing auth token');
        const response = await fetch(`${API_URL}${basePath}${path}`, {
            method,
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            ...(body ? { body: JSON.stringify(body) } : {}),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || `Request failed (${response.status})`);
        return data;
    }, [basePath, getToken]);

    const loadStores = useCallback(async () => {
        try {
            const data = await call('/stores');
            setStores(data.stores || []);
            setActiveId(data[activeKey] || null);
            return data;
        } catch (err) {
            setStores([]);
            setActiveId(null);
            return null;
        }
    }, [call, activeKey]);

    // Optimistic: the dropdown moves immediately and reverts if the call fails,
    // so a slow network never looks like an ignored click.
    const switchStore = useCallback(async (nextId) => {
        if (!nextId || nextId === activeId) return null;
        const previousId = activeId;
        setIsSwitching(true);
        setError('');
        setActiveId(nextId);
        try {
            const data = await call('/stores/active', {
                method: 'POST', body: { [idField]: nextId },
            });
            await loadStores();
            onChange?.(nextId);
            return data.store || null;
        } catch (err) {
            setActiveId(previousId);
            setError(err.message || 'Could not switch account');
            return null;
        } finally {
            setIsSwitching(false);
        }
    }, [activeId, call, idField, loadStores, onChange]);

    // Scopes the disconnect to one account; the backend promotes another
    // connected account to main and reports whether any remain.
    const disconnectStore = useCallback(async (targetId) => {
        const data = await call('/disconnect', {
            method: 'POST', body: targetId ? { [idField]: targetId } : {},
        });
        setActiveId(data[activeKey] || null);
        await loadStores();
        onChange?.(data[activeKey] || null);
        return data;
    }, [call, idField, activeKey, loadStores, onChange]);

    return {
        stores, activeId, isSwitching, error,
        setError, loadStores, switchStore, disconnectStore,
    };
};

export default useConnectedStores;
